// src/pages/Futures/FuturesOrderBook.js
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, useTheme, Button, Select, MenuItem, FormControl,
  IconButton, Tooltip, useMediaQuery, LinearProgress, Paper, Divider, Skeleton, Alert
} from '@mui/material';
import { fetchOrderBook, updateOrderBook, setOrderBookError } from '../../store/Slices/assetSlice';
import { styled } from '@mui/system';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { green, red } from '@mui/material/colors';

const COLORS = {
  GREEN: '#0ECB81',
  RED: '#F6465D',
  YELLOW: '#F0B90B',
};

const HeaderCell = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.8rem',
}));

const Cell = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
}));

const FuturesOrderBook = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const { selectedCoin } = useSelector((s) => s.futures);
  const { orderBook, orderBookLoading, error } = useSelector((s) => s.assets);
  const [displayLimit, setDisplayLimit] = useState(14);
  const [group, setGroup] = useState(0);
  const [view, setView] = useState('both'); // both | bids | asks
  const [localError, setLocalError] = useState(null);

  const abortRef = useRef(null);
  const fetchTimer = useRef(null);
  const mounted = useRef(false);

  const formatNum = (v, d = 4) => (typeof v === 'number' && isFinite(v) ? v.toFixed(d) : '--');

  // Grouping
  const groupOrders = (orders, g) => {
    if (!Array.isArray(orders) || !g) return orders || [];
    const map = {};
    for (const it of orders) {
      const price = it.price || 0;
      const key = Math.floor(price / g) * g;
      if (!map[key]) map[key] = { price: key, amount: 0, total: 0 };
      map[key].amount += it.amount || 0;
      map[key].total += it.total || 0;
    }
    return Object.values(map);
  };

  const doFetch = async (signal) => {
    if (!selectedCoin) return;
    try {
      const res = await fetch(`https://api.binance.com/api/v3/depth?symbol=${selectedCoin.toUpperCase()}&limit=50`, { signal });
      const data = await res.json();
      if (!data || !Array.isArray(data.bids) || !Array.isArray(data.asks)) {
        throw new Error('Invalid order book response');
      }
      const formatted = {
        bids: data.bids.map(([p, a]) => ({ price: +p, amount: +a, total: (+p) * (+a) })),
        asks: data.asks.map(([p, a]) => ({ price: +p, amount: +a, total: (+p) * (+a) })),
        currentPrice: data.bids.length ? +data.bids[0][0] : orderBook?.currentPrice || 0,
      };
      dispatch(updateOrderBook(formatted));
      setLocalError(null);
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('OrderBook fetch error:', e);
      setLocalError(e.message);
      dispatch(setOrderBookError(e.message));
    }
  };

  // initial + interval fetch with backoff
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; clearInterval(fetchTimer.current); abortRef.current?.abort(); };
  }, []);

  useEffect(() => {
    if (!selectedCoin) return;
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    let tries = 0;

    const run = async () => {
      tries++;
      await doFetch(abortRef.current.signal);
      // schedule next
      if (!mounted.current) return;
      const delay = Math.min(2000 * tries, 8000); // mild backoff
      fetchTimer.current = setTimeout(run, 2000); // keep 2s pull; errors handled locally
    };
    // kick
    run();

    return () => { clearTimeout(fetchTimer.current); abortRef.current?.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCoin]);

  const bids = useMemo(() => {
    const arr = orderBook?.bids || [];
    const g = groupOrders([...arr].sort((a, b) => b.price - a.price), group);
    return g.slice(0, displayLimit);
  }, [orderBook?.bids, group, displayLimit]);

  const asks = useMemo(() => {
    const arr = orderBook?.asks || [];
    const g = groupOrders([...arr].sort((a, b) => a.price - b.price), group);
    return g.slice(0, displayLimit);
  }, [orderBook?.asks, group, displayLimit]);

  const maxAmount = useMemo(() => {
    const a1 = (bids || []).map(b => b.amount);
    const a2 = (asks || []).map(a => a.amount);
    return Math.max(0, ...a1, ...a2);
  }, [bids, asks]);

  const currentPrice = orderBook?.currentPrice || 0;

  const Retry = () => (
    <Box sx={{ textAlign: 'center', py: 1 }}>
      <Button
        startIcon={<RestartAltIcon />}
        variant="contained"
        onClick={() => doFetch(abortRef.current?.signal)}
        sx={{ bgcolor: theme.palette.mode === 'dark' ? COLORS.YELLOW : 'primary.main' }}
      >
        Retry
      </Button>
    </Box>
  );

  return (
    <Paper sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {orderBookLoading && <LinearProgress sx={{ height: 2, mb: 1 }} />}

      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Order Book — {selectedCoin || '--'}
        </Typography>

        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <FormControl size="small">
            <Select value={displayLimit} onChange={(e) => setDisplayLimit(e.target.value)}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={14}>14</MenuItem>
              <MenuItem value={20}>20</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <Select value={group} onChange={(e) => setGroup(e.target.value)}>
              <MenuItem value={0}>No Group</MenuItem>
              <MenuItem value={0.01}>0.01</MenuItem>
              <MenuItem value={0.1}>0.1</MenuItem>
              <MenuItem value={1}>1</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Bids only">
            <IconButton onClick={() => setView('bids')} size="small">
              <CandlestickChartIcon sx={{ color: green[500] }} fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Asks only">
            <IconButton onClick={() => setView('asks')} size="small">
              <CandlestickChartIcon sx={{ color: red[500] }} fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Both">
            <IconButton onClick={() => setView('both')} size="small">
              <CandlestickChartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Current Price */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        py: 0.5, mb: 1, borderRadius: 1, bgcolor: theme.palette.mode === 'dark' ? 'rgba(240,185,11,0.08)' : 'rgba(240,185,11,0.12)'
      }}>
        <Typography sx={{ color: theme.palette.mode === 'dark' ? COLORS.YELLOW : 'text.primary', fontWeight: 700 }}>
          {currentPrice ? formatNum(currentPrice, 2) : '--'} USDT
        </Typography>
      </Box>

      {/* Headers */}
      <Box sx={{ display: 'flex', px: 1, mb: 0.5 }}>
        <HeaderCell sx={{ flex: 1 }}>Price (USDT)</HeaderCell>
        <HeaderCell sx={{ flex: 1, textAlign: 'center' }}>Amount</HeaderCell>
        <HeaderCell sx={{ flex: 1, textAlign: 'right' }}>Total</HeaderCell>
      </Box>

      {/* Lists */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minHeight: 0 }}>
        {/* Asks */}
        {(view === 'both' || view === 'asks') && (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {asks.length ? asks.map((a, i) => (
              <Box key={`ask-${i}`} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.25, position: 'relative',
                '&:hover': { bgcolor: 'action.hover' } }}>
                <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'transparent' }}>
                  <Box sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: `${maxAmount ? (a.amount / maxAmount) * 100 : 0}%`,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(246,70,93,0.12)' : 'rgba(246,70,93,0.18)' }} />
                </Box>
                <Cell sx={{ flex: 1, color: COLORS.RED }}>{formatNum(a.price, 2)}</Cell>
                <Cell sx={{ flex: 1, textAlign: 'center', color: 'text.primary' }}>{formatNum(a.amount, 4)}</Cell>
                <Cell sx={{ flex: 1, textAlign: 'right', color: 'text.secondary' }}>{formatNum(a.total, 2)}</Cell>
              </Box>
            )) : (
              <Skeleton variant="rectangular" height={isMobile ? 120 : 160} sx={{ borderRadius: 1 }} />
            )}
          </Box>
        )}

        <Divider sx={{ my: 0.5 }} />

        {/* Bids */}
        {(view === 'both' || view === 'bids') && (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {bids.length ? bids.map((b, i) => (
              <Box key={`bid-${i}`} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.25, position: 'relative',
                '&:hover': { bgcolor: 'action.hover' } }}>
                <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'transparent' }}>
                  <Box sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: `${maxAmount ? (b.amount / maxAmount) * 100 : 0}%`,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(14,203,129,0.12)' : 'rgba(14,203,129,0.18)' }} />
                </Box>
                <Cell sx={{ flex: 1, color: COLORS.GREEN }}>{formatNum(b.price, 2)}</Cell>
                <Cell sx={{ flex: 1, textAlign: 'center', color: 'text.primary' }}>{formatNum(b.amount, 4)}</Cell>
                <Cell sx={{ flex: 1, textAlign: 'right', color: 'text.secondary' }}>{formatNum(b.total, 2)}</Cell>
              </Box>
            )) : (
              <Skeleton variant="rectangular" height={isMobile ? 120 : 160} sx={{ borderRadius: 1 }} />
            )}
          </Box>
        )}
      </Box>

      {(error || localError) && (
        <Box sx={{ mt: 1 }}>
          <Alert severity="warning" sx={{ mb: 1 }}>{error || localError}</Alert>
          <Retry />
        </Box>
      )}
    </Paper>
  );
};

export default FuturesOrderBook;
